import objectUtils from "@/common/utils/objectUtils";

export const attrNames = {
  SPAWN: "spawn",
  AUTO_PLAY: "autoplay",
  SHARE_LINK: "sln",
  SHOWN_AS: "sa",
  BANNER_SOURCE: "abs",
  BANNER_RADIUS: "abr",
  TARGET_WINDOW: "win",
  MOBILE_TARGET_WINDOW: "mwin",
  SHOW_SHOP_WINDOW: "sw",
  SHOW_SHOP_WINDOW_ON_MARK: "swm",
  DEFAULT_SHOP_WINDOW: "dsw",
  ADD_ALL_TO_FAVORITES: "af",
  CLEAR_FAVORITES: "cf",
  PARENT_URL: "pt",
  HOST_URL: "hu",
  BASE_LINK: "bln",
  CAMPAIGN_ID: "cid",
  EXPERIENCE_ID: "expid",
  HOST: "env",
  SHARE_HOST: "senv",
  ASSET_HOST: "aenv",
  PLAY_HOST: "penv",
  API_HOST: "apienv",
  MOBILE: "mobile",
  PLAY_ICON_URL: "pi",
  PLAY_ICON_HOVER_URL: "pih",
  PLAY_ICON_SIZE: "pis",
  VISIBLE_METHOD_STEPS: "vms",
  SUBSTITUTION: "sub",
  SEEK_TO: "t",
  HIGHLIGHT_PRODUCT: "product",
  OPEN_TAB: "tab",
  HIGHLIGHT_STEP: "step",
};

export const shownAsType = {
  OVERLAY: "ol",
  WINDOW: "win",
  EMBED: "em",
  PAGE: "pg",
};

/**
 * Embed attributes by short name (`data-pix-<name>` or `?pix-<name>`). A query
 * flag without a value is stored as `true`; everything else is a string.
 */
export type Attrs = Record<string, string | true>;

/** The attr's text, or `undefined` when it is a bare `?pix-<name>` flag or empty. */
export function attrValue(
  value: string | true | undefined,
): string | undefined {
  if (value === true || !value) {
    return undefined;
  }
  return value;
}

// Attrs whose value is text (host, URL, size): a bare flag or empty value means "not set".
const TEXT_ATTRS: Record<string, boolean> = {
  [attrNames.HOST_URL]: true,
  [attrNames.BASE_LINK]: true,
  [attrNames.HOST]: true,
  [attrNames.SHARE_HOST]: true,
  [attrNames.ASSET_HOST]: true,
  [attrNames.PLAY_HOST]: true,
  [attrNames.API_HOST]: true,
  [attrNames.BANNER_SOURCE]: true,
  [attrNames.PLAY_ICON_SIZE]: true,
};

/** True when `key` is a text attr and `value` is a bare flag or empty. */
function isUnsetTextAttr(key: string, value: string | true | undefined) {
  return TEXT_ATTRS[key] === true && attrValue(value) === undefined;
}

type QuerySettings = Record<string, string>;

export default class ExperienceAttributes {
  static MESSAGE_PREFIX = "_pixmoto_";

  declare _attrs: Attrs;
  declare _linkSubs: Record<string, string | true>;

  static attrsFromElement(element: Element): Attrs {
    const attrs: Attrs = {};
    for (let i = 0, atts = element.attributes, n = atts.length; i < n; i++) {
      const att = atts[i];
      if (att.nodeName.startsWith("data-pix-")) {
        const name = att.nodeName.substring(9, att.nodeName.length),
          val = att.nodeValue;
        if (val) {
          attrs[name] = val;
        }
      }
    }

    if (attrs[attrNames.EXPERIENCE_ID] === undefined) {
      attrs[attrNames.EXPERIENCE_ID] = element.getAttribute(
        "data-idexperience",
      ) as string;
    }

    return attrs;
  }

  static attrsFromQuery(qs: QuerySettings, prefixed?: boolean): Attrs {
    let attrs: Attrs = {},
      name: string | null,
      val: string | null;

    for (const key in qs) {
      name = null;
      val = null;
      if (prefixed) {
        if (key.startsWith("pix-")) {
          name = key.substring(4, key.length);
          val = qs[key];
        }
      } else {
        name = key;
        val = qs[key];
      }

      if (name) {
        if (val) {
          attrs[name] = val;
        } else {
          attrs[name] = true;
        }
      }
    }

    return attrs;
  }

  static attrsFromCombinedAttributes(
    element: Element,
    qs: QuerySettings,
    prefixed?: boolean,
  ): Attrs {
    const ea = ExperienceAttributes.attrsFromElement(element),
      qa = ExperienceAttributes.attrsFromQuery(qs, prefixed);
    for (const key in qa) {
      // qa values win.
      ea[key] = qa[key];
    }

    // Anchor `data-pix-autoplay` wins over parent-page `?pix-autoplay=…`.
    if (element && element.hasAttribute("data-pix-autoplay")) {
      const fromEl = ExperienceAttributes.attrsFromElement(element);
      if (fromEl[attrNames.AUTO_PLAY] !== undefined) {
        ea[attrNames.AUTO_PLAY] = fromEl[attrNames.AUTO_PLAY];
      }
    }

    return ea;
  }

  static fromElement(element: Element) {
    return new ExperienceAttributes(
      ExperienceAttributes.attrsFromElement(element),
    );
  }

  static fromQuery(qs: QuerySettings, prefixed?: boolean) {
    return new ExperienceAttributes(
      ExperienceAttributes.attrsFromQuery(qs, prefixed),
    );
  }

  static fromCombinedAttributes(
    element: Element,
    qs: QuerySettings,
    prefixed?: boolean,
  ) {
    return new ExperienceAttributes(
      ExperienceAttributes.attrsFromCombinedAttributes(element, qs, prefixed),
    );
  }

  constructor(attrs?: Attrs | null) {
    this._attrs = attrs || {};
    this._linkSubs = {};

    const keys = Object.keys(this._attrs);
    const sub = `${attrNames.SUBSTITUTION}-`;
    const subLen = sub.length;

    for (let n = 0, max = keys.length; n < max; n++) {
      const key = keys[n];
      if (key && key.startsWith(sub)) {
        this._linkSubs[`{{${key.substring(subLen)}}}`] = this._attrs[key];
      }
    }
  }

  toBoolean(value: unknown, dflt?: boolean): boolean {
    if (value === undefined && dflt !== undefined) {
      return dflt;
    }
    return (
      value === true ||
      ((typeof value === "string" || value instanceof String) &&
        (value === "" || value.toUpperCase() !== "FALSE"))
    );
  }

  isAutospawn() {
    return this.toBoolean(this._attrs[attrNames.SPAWN]);
  }

  hasAutoPlaySetting() {
    return (
      Object.prototype.hasOwnProperty.call(this._attrs, attrNames.AUTO_PLAY) ||
      Object.prototype.hasOwnProperty.call(
        this._attrs,
        `pix-${attrNames.AUTO_PLAY}`,
      )
    );
  }

  autoPlay() {
    return (
      this.toBoolean(this._attrs[attrNames.AUTO_PLAY]) ||
      this.toBoolean(this._attrs[`pix-${attrNames.AUTO_PLAY}`])
    );
  }

  /**
   * Embed `data-pix-autoplay` / `pix-autoplay` wins over experience video defaults.
   */
  resolveAutoPlay(experienceWantsAutoPlay: unknown): boolean {
    if (this.hasAutoPlaySetting()) {
      return this.autoPlay() === true;
    }
    return experienceWantsAutoPlay === true;
  }

  shareLink() {
    return this._attrs[attrNames.SHARE_LINK] || null;
  }

  shownAs() {
    return this._attrs[attrNames.SHOWN_AS] || null;
  }

  isOverlay() {
    return this.shownAs() === shownAsType.OVERLAY;
  }

  isWindow() {
    return this.shownAs() === shownAsType.WINDOW;
  }

  isEmbed() {
    return this.shownAs() === shownAsType.EMBED;
  }

  isPage() {
    return this.shownAs() === shownAsType.PAGE;
  }

  bannerSource() {
    return attrValue(this._attrs[attrNames.BANNER_SOURCE]) ?? null;
  }

  bannerRadius() {
    return this._attrs[attrNames.BANNER_RADIUS] || null;
  }

  targetWindow() {
    return this._attrs[attrNames.TARGET_WINDOW] || null;
  }

  mobileTargetWindow() {
    return this._attrs[attrNames.MOBILE_TARGET_WINDOW] || null;
  }

  showShopWindow() {
    return this.toBoolean(this._attrs[attrNames.SHOW_SHOP_WINDOW], true);
  }

  showShopWindowOnMarker() {
    return this.toBoolean(
      this._attrs[attrNames.SHOW_SHOP_WINDOW_ON_MARK],
      false,
    );
  }

  defaultShopWindow() {
    return this._attrs[attrNames.DEFAULT_SHOP_WINDOW] || null;
  }

  addFavorites() {
    return this.toBoolean(this._attrs[attrNames.ADD_ALL_TO_FAVORITES]);
  }

  clearFavorites() {
    return this.toBoolean(this._attrs[attrNames.CLEAR_FAVORITES]);
  }

  parentUrl() {
    return (
      this._attrs[attrNames.PARENT_URL] ||
      this._attrs[`pix-${attrNames.PARENT_URL}`] ||
      null
    );
  }

  /**
   * The target origin is where to send messages. It is typically the
   * parent of the embedded iframe.
   */
  targetOrigin() {
    const parentUrl = this.parentUrl();
    // If the parent url does not start with http, we use the wildcard.
    // This allows us to send to iframes with srcdoc.
    if (parentUrl === true || (parentUrl && !parentUrl.startsWith("http"))) {
      return "*";
    }
    return parentUrl;
  }

  hostUrl() {
    return this._attrs[attrNames.HOST_URL] || null;
  }

  baseLink() {
    return attrValue(this._attrs[attrNames.BASE_LINK]) ?? null;
  }

  campaignId() {
    return this._attrs[attrNames.CAMPAIGN_ID] || null;
  }

  linkSubs() {
    return this._linkSubs;
  }

  replaceAll(str: string, mapObj: Record<string, string | true>): string {
    const keys = Object.keys(mapObj);
    if (keys.length === 0) {
      return str;
    }
    const re = new RegExp(Object.keys(mapObj).join("|"), "g");

    return str.replace(re, function (matched) {
      return encodeURIComponent(mapObj[matched]);
    });
  }

  updatedLinkFromUrl(url: string | null | undefined): string | null {
    if (!url) {
      return null;
    }
    let link = url;

    const baseLink = this.baseLink();
    if (baseLink !== null) {
      const encodedUrl = this.replaceAll(url, this._linkSubs);
      const subs = objectUtils.extend(
        {
          "{{URL}}": encodedUrl,
        },
        this._linkSubs,
      );

      link = this.replaceAll(baseLink, subs);
    } else {
      link = this.replaceAll(url, this._linkSubs);
    }

    return link;
  }

  getExperienceId() {
    return this._attrs[attrNames.EXPERIENCE_ID];
  }

  getUrl() {
    return this._attrs.url || null;
  }

  getServer(server: string, env: string | true | undefined): string {
    let ret: string;
    const host = attrValue(env);
    if (host) {
      /*
       * Subdomain shorthand: "staging" -> staging.play.pixmoto.com
       * Full host: contains a dot (FQDN / IPv4) or colon (host:port) or plain localhost.
       */
      const isFullHost =
        host.indexOf(".") >= 0 ||
        host.indexOf(":") >= 0 ||
        host.toLowerCase() === "localhost";
      if (isFullHost) {
        ret = host;
      } else {
        ret = `${host}.${server}`;
      }
    } else {
      ret = server;
    }
    return ret;
  }

  getSharedServer() {
    return this.getServer(
      "cdn.pixmoto.com",
      attrValue(this._attrs[attrNames.SHARE_HOST]) ||
        this._attrs[attrNames.HOST],
    );
  }

  getAssetServer() {
    return this.getServer(
      "assets.pixmoto.com",
      attrValue(this._attrs[attrNames.ASSET_HOST]) ||
        this._attrs[attrNames.HOST],
    );
  }

  getPlayServer() {
    return this.getServer(
      "play.pixmoto.com",
      attrValue(this._attrs[attrNames.PLAY_HOST]) ||
        this._attrs[attrNames.HOST],
    );
  }

  /** Base hostname for publisher experience API (no protocol). */
  getApiBaseHost() {
    return this.getServer(
      "admin-api.pixmoto.com",
      attrValue(this._attrs[attrNames.API_HOST]) || this._attrs[attrNames.HOST],
    );
  }

  /**
   * Flat embed attrs for /publisher/experience query string (excludes play-route-only keys).
   */
  getAttributesForExperienceApiQuery() {
    const dontCopy: Record<string, boolean> = {};
    dontCopy[attrNames.HOST_URL] = true;
    dontCopy[attrNames.PARENT_URL] = true;
    dontCopy[attrNames.SPAWN] = true;
    dontCopy[attrNames.SHOWN_AS] = true;

    const out: Attrs = {};
    for (const key in this._attrs) {
      if (!Object.prototype.hasOwnProperty.call(this._attrs, key)) {
        continue;
      }
      if (dontCopy[key]) {
        continue;
      }
      const val = this._attrs[key];
      if (
        val === undefined ||
        val === null ||
        val === "" ||
        isUnsetTextAttr(key, val)
      ) {
        continue;
      }
      out[key] = val;
    }
    return out;
  }

  getIsForcedMobile() {
    const mobile = this._attrs[attrNames.MOBILE];
    return !(mobile == null || mobile === "false");
  }

  hasPlayIcon() {
    return this.getCustomPlayIconUrl() !== "false";
  }

  getCustomPlayIconUrl() {
    return this._attrs[attrNames.PLAY_ICON_URL];
  }

  getCustomPlayIconSize(): { width: number; height: number } | undefined {
    const pis = attrValue(this._attrs[attrNames.PLAY_ICON_SIZE]);
    if (pis) {
      const s = pis.split("x");
      const w = parseInt(s[0]);
      let h = w;
      if (s.length > 1) {
        h = parseInt(s[1]);
      }
      return { width: w, height: h };
    }
  }

  getPlayIconUrl() {
    const custom = this.getCustomPlayIconUrl();
    if (custom === "false") {
      return;
    }
    return custom || `//${this.getSharedServer()}/play/pixmoto-play-icon.png`;
  }

  getPlayIconSize() {
    let size = this.getCustomPlayIconSize();
    if (!size) {
      size = { width: 200, height: 200 };
    }
    return size;
  }

  getPlayIconHoverUrl() {
    return (
      this._attrs[attrNames.PLAY_ICON_HOVER_URL] ||
      `//${this.getSharedServer()}/play/pixmoto-play-icon-heart-hover.png`
    );
  }

  getVisibleMethodSteps() {
    const val = this._attrs[attrNames.VISIBLE_METHOD_STEPS];
    if (val) {
      const ret = parseInt(String(val));
      if (!isNaN(ret)) {
        return ret;
      }
    }
  }

  /**
   * Play-page deep-link: seconds to seek the video to on initial load (?t=30).
   * Returns a non-negative number, or undefined when absent/invalid.
   */
  getSeekToSeconds() {
    const val = this._attrs[attrNames.SEEK_TO];
    if (val) {
      const ret = parseFloat(String(val));
      if (!isNaN(ret) && ret >= 0) {
        return ret;
      }
    }
  }

  /**
   * Play-page deep-link: product (article) id to open the shop window on (?product=123).
   * Returns an integer id, or undefined when absent/invalid.
   */
  getHighlightProductId() {
    const val = this._attrs[attrNames.HIGHLIGHT_PRODUCT];
    if (val) {
      const ret = parseInt(String(val));
      if (!isNaN(ret)) {
        return ret;
      }
    }
  }

  /**
   * Play-page deep-link: shop-window tab to open on initial load (?tab=method).
   * Returns the lowercased token (mapped to a shop window by the consumer), or
   * undefined when absent.
   */
  getOpenTab() {
    const val = this._attrs[attrNames.OPEN_TAB];
    if (
      val &&
      (typeof val === "string" || (val as unknown) instanceof String)
    ) {
      return `${val}`.toLowerCase();
    }
  }

  /**
   * Play-page deep-link: 1-based method step to open on initial load (?step=3 →
   * the third step). Returns a positive integer, or undefined when absent/invalid.
   */
  getHighlightStepIndex() {
    const val = this._attrs[attrNames.HIGHLIGHT_STEP];
    if (val) {
      const ret = parseInt(String(val));
      if (!isNaN(ret) && ret >= 1) {
        return ret;
      }
    }
  }

  getPlayUrl(isAuto?: boolean, shownAs?: string, prefixed?: boolean): string {
    const dontCopy: Record<string, boolean> = {};
    dontCopy[attrNames.HOST_URL] = true;
    dontCopy[attrNames.PARENT_URL] = true;
    dontCopy[attrNames.SPAWN] = true;
    dontCopy[attrNames.SHOWN_AS] = true;

    let idexperience = this._attrs.expid,
      hu = attrValue(this._attrs[attrNames.HOST_URL]),
      playBase = `//${this.getPlayServer()}`,
      // Use HOST_URL whenever set (including overlay): URL-based resolution and mapped pages need correct host.
      ret = hu || playBase + (idexperience ? `/${idexperience}` : "/"),
      qs = "",
      prefix = prefixed ? "pix-" : "";

    for (const key in this._attrs) {
      if (!(key in dontCopy) && !isUnsetTextAttr(key, this._attrs[key])) {
        const name = encodeURIComponent(key);
        const val = encodeURIComponent(this._attrs[key]);

        if (val) {
          if (qs.length > 0) {
            qs += "&";
          }
          qs += `${prefix + name}=${val}`;
        }
      }
    }

    const pt =
      location.protocol +
      "//" +
      location.hostname +
      (location.port ? `:${location.port}` : "");

    if (qs.length > 0) {
      qs += "&";
    }

    qs += `${prefix + attrNames.PARENT_URL}=${encodeURIComponent(pt)}`;

    if (isAuto === true) {
      qs += `&${prefix}${attrNames.SPAWN}=true`;
    }

    if (shownAs) {
      qs += `&${prefix}${attrNames.SHOWN_AS}=${shownAs}`; // signify shownAs is initiating.
    }

    if (qs.length > 0) {
      let hash = "";
      const hashIdx = ret.indexOf("#");
      if (hashIdx >= 0) {
        hash = ret.substring(hashIdx);
        ret = ret.substring(0, hashIdx);
      }
      ret += (ret.indexOf("?") >= 0 ? "&" : "?") + qs + hash;
    }

    return ret;
  }
}
